import { z } from "zod";
import { AgentCommandArgs, AgentCommandId, AgentRole } from "./agent-roles.js";
import { CostPreview } from "./costs.js";
import { TerminalAccessLevel } from "./terminal.js";

/*
 * Confirmation cards of the AI assistant (docs/assistant.md, "Changes"):
 * when the assistant wants to change something it never runs it; the API
 * turns the proposal into a card that shows exactly the request, and only
 * the person's click in the same browser session runs it, once.
 */

/** The risk classes a card can carry: the writes (docs/permissions.md). */
export const AssistantCardRisk = z.enum(["operate", "change", "create", "destroy"]);
export type AssistantCardRisk = z.infer<typeof AssistantCardRisk>;

export const AssistantCardStatus = z.enum([
  /** Waiting for the person: «Выполнить» or «Отклонить». */
  "pending",
  "running",
  "done",
  "failed",
  "rejected",
  /** Ten minutes passed, or the browser session it was made for ended. */
  "expired",
]);
export type AssistantCardStatus = z.infer<typeof AssistantCardStatus>;

/**
 * What a change interrupts, as a key the panel words (`assistant.cards.interrupts.<key>`):
 * `restart` (the server restarts), `power_off`, `needs_off` (works only on a powered-off
 * server), `disk_overwritten`, `traffic` (connections may drop), `data_loss` (deleted for good).
 */
export const AssistantCardInterrupt = z.enum([
  "restart",
  "power_off",
  "needs_off",
  "disk_overwritten",
  "traffic",
  "data_loss",
]);
export type AssistantCardInterrupt = z.infer<typeof AssistantCardInterrupt>;

/**
 * What a card runs: `request` — one request of our API (a panel change);
 * `command` — one write command of the server's Autopilot agent role
 * (capability `actions`: `agent-rw` or `agent-manage`);
 * `terminal` — one command typed into the person's own open terminal session
 * (pair mode, docs/assistant.md "Working together in the terminal").
 */
export const AssistantCardKind = z.enum(["request", "command", "terminal"]);
export type AssistantCardKind = z.infer<typeof AssistantCardKind>;

/** A write command of the agent role: the exact id and arguments, the server and its role. */
export const AssistantCardCommand = z.object({
  serverId: z.uuid(),
  command: AgentCommandId,
  args: AgentCommandArgs,
  /** The server's role when the card was made (a role with `actions` runs it). */
  role: AgentRole.nullable(),
  /** The catalogue's warning lines for this command, in English (the panel words them by id). */
  warnings: z.array(z.string()),
});
export type AssistantCardCommand = z.infer<typeof AssistantCardCommand>;

/**
 * A command's class (docs/assistant.md, "Command classes"): a `write`
 * changes without removing anything, a `destructive` one removes, overwrites,
 * stops or substantially changes, a `dangerous` one can lock the person out
 * or lose the server's data.
 */
export const TerminalCommandLevel = z.enum(["write", "destructive", "dangerous"]);
export type TerminalCommandLevel = z.infer<typeof TerminalCommandLevel>;

/**
 * A command for the person's own terminal session (pair mode): exactly the
 * text typed, where, and why it needs the person — a click for a write or a
 * destructive one, a fresh second factor for a dangerous one.
 */
export const AssistantCardTerminal = z.object({
  sessionId: z.uuid(),
  serverId: z.uuid(),
  serverName: z.string(),
  unixUser: z.string(),
  command: z.string(),
  level: TerminalCommandLevel,
  /** Why it is not read-only, in English (the classifier's words). */
  reasons: z.array(z.string()),
  /** Why the assistant runs it, in its words (redacted). */
  why: z.string(),
  /**
   * What will be removed or changed, one line in the assistant's words
   * (redacted): the packages `apt-get -s` showed, the service stopped…;
   * null for a plain write and for cards made before 2026-10-22.
   */
  effect: z.string().nullable(),
});
export type AssistantCardTerminal = z.infer<typeof AssistantCardTerminal>;

export const AssistantCard = z.object({
  id: z.uuid(),
  kind: AssistantCardKind,
  conversationId: z.uuid(),
  /** The answer that proposed it. */
  messageId: z.uuid(),
  /** The tool the model called and the catalogue action it needs. */
  tool: z.string(),
  action: z.string(),
  risk: AssistantCardRisk,
  projectId: z.uuid(),
  projectName: z.string(),
  /** What the assistant says the change does, one sentence (redacted). */
  title: z.string(),
  /** The resource it acts on, when it acts on one. */
  target: z.object({ id: z.string(), name: z.string().nullable(), noun: z.string() }).nullable(),
  /** The exact request that runs (`request` cards): method, path below `/api/v1`, JSON body. */
  method: z.enum(["POST", "PUT", "PATCH", "DELETE"]).nullable(),
  path: z.string().nullable(),
  body: z.unknown().nullable(),
  /** The exact command that runs (`command` cards). */
  command: AssistantCardCommand.nullable(),
  /** The exact command typed into the person's terminal (`terminal` cards). */
  terminal: AssistantCardTerminal.nullable(),
  /**
   * SHA-256 of what runs (`requestFingerprint` of the request, `callHash` of
   * the command, `terminalHash` of the session and the typed text); the card runs only that.
   */
  paramsHash: z.string().regex(/^[0-9a-f]{64}$/),
  /** The cost preview and the budget rule (docs/budgets.md); null when the change adds no cost. */
  cost: CostPreview.nullable(),
  interrupts: AssistantCardInterrupt.nullable(),
  /** Destroying asks to type this name (the resource's name, or its id when it has none). */
  confirmName: z.string().nullable(),
  status: AssistantCardStatus,
  /** What the run answered (HTTP status, the panel's words) and the operation it started. */
  result: z
    .object({
      status: z.number().int(),
      text: z.string(),
      reason: z.string().nullable(),
      operationId: z.uuid().nullable(),
    })
    .nullable(),
  createdAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
  decidedAt: z.iso.datetime().nullable(),
  /**
   * The one-time token «Выполнить» sends: only for the browser session the
   * card was made in, while it is pending. Never given to the model.
   */
  token: z.string().nullable(),
});
export type AssistantCard = z.infer<typeof AssistantCard>;

/**
 * Where the person decides a card: the answer about its outcome gives times
 * in their time zone and falls back to their panel's language.
 */
export const AssistantDecisionPlace = z.object({
  timeZone: z
    .string()
    .regex(/^[A-Za-z0-9_+\-/]{1,64}$/)
    .optional(),
  language: z.enum(["ru", "en"]).optional(),
});
export type AssistantDecisionPlace = z.infer<typeof AssistantDecisionPlace>;

/**
 * `POST /assistant/conversations/:id/messages/:messageId/run` — ▶ «Выполнить»
 * under a command block of the assistant's answer `messageId`: that block's
 * exact text goes into the person's terminal session as the assistant's
 * `terminal_run` (classified and carded like any); the conversation keeps the
 * press as an event, not as a message of the person. Answers a
 * `text/event-stream` like `POST …/messages`.
 */
export const RunAssistantCommandRequest = z.object({
  /** The block's text exactly as the answer holds it (one shell line). */
  command: z.string().min(1).max(2000),
  /** The person's own open SSH terminal session where the Autopilot's access is above off. */
  sessionId: z.uuid(),
  place: AssistantDecisionPlace.optional(),
});
export type RunAssistantCommandRequest = z.input<typeof RunAssistantCommandRequest>;

/** `POST …/cards/:cardId/confirm`: the token, and the typed name for destroying. */
export const ConfirmAssistantCardRequest = z.object({
  token: z.string().min(1).max(200),
  confirmName: z.string().max(300).optional(),
  place: AssistantDecisionPlace.optional(),
});
export type ConfirmAssistantCardRequest = z.input<typeof ConfirmAssistantCardRequest>;

/** `POST …/cards/:cardId/reject`: nothing but where the person is. */
export const RejectAssistantCardRequest = z.object({ place: AssistantDecisionPlace.optional() });
export type RejectAssistantCardRequest = z.input<typeof RejectAssistantCardRequest>;

/** Whether changes are unlocked for this person in this browser session (the second factor). */
export const AssistantChangeMode = z.object({
  mode: z.enum(["read", "change"]),
  /** Until when changes stay unlocked; null in read mode. */
  changesUntil: z.iso.datetime().nullable(),
});
export type AssistantChangeMode = z.infer<typeof AssistantChangeMode>;

/** How an organisation lets the assistant change things (stricter than the defaults only). */
export const AssistantChangePolicy = z.object({
  /** Off: the assistant only reads and never proposes a card. */
  allowChanges: z.boolean(),
  /** How long a second factor unlocks changes, 5 minutes to 12 hours. */
  changeWindowMinutes: z.number().int().min(5).max(720),
  /** Every card asks for the second factor again. */
  stepUpEveryChange: z.boolean(),
  /**
   * The highest «Доступ Автопилота к терминалу» people may give the assistant
   * in their own terminal sessions (docs/assistant.md "Terminal access");
   * `off`: nobody can; default `auto`.
   */
  terminalAccessMax: TerminalAccessLevel,
});
export type AssistantChangePolicy = z.infer<typeof AssistantChangePolicy>;

/** The longest a second factor unlocks changes (owner's decision 1). */
export const ASSISTANT_CHANGE_WINDOW_MAX_MINUTES = 720;
/** How long a card can be confirmed after it was proposed. */
export const ASSISTANT_CARD_TTL_MINUTES = 10;
